import type { WebsiteEnvironment, WebsiteStatus } from "./field-map.js";

/** RFC v0.5.3 §2.2 - website list item shape for Primary Website selection. */
export interface WebsiteListItem {
  readonly id: string;
  readonly domain: string;
  readonly environment: WebsiteEnvironment;
  readonly status: WebsiteStatus;
  readonly technology: string | null;
  readonly ownerMembershipId: string;
  readonly projectId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export type PrimaryWebsiteResolution =
  | { readonly kind: "none" }
  | { readonly kind: "known"; readonly website: WebsiteListItem }
  | { readonly kind: "chooser"; readonly candidates: readonly WebsiteListItem[] };

/**
 * RFC v0.5.3 §2.2 - deterministic Primary Website selection.
 * Caller must pass the **full** non-archived client website list (exhaust pagination).
 *
 * 1. Exactly one → that website
 * 2. More than one → sole PRODUCTION when unambiguous; else chooser
 * 3. None → empty
 */
export const resolvePrimaryWebsite = (
  websites: readonly WebsiteListItem[],
): PrimaryWebsiteResolution => {
  if (websites.length === 0) {
    return { kind: "none" };
  }
  if (websites.length === 1) {
    const only = websites[0];
    if (only === undefined) {
      return { kind: "none" };
    }
    return { kind: "known", website: only };
  }

  const production = websites.filter((website) => website.environment === "PRODUCTION");
  if (production.length === 1) {
    const sole = production[0];
    if (sole === undefined) {
      return { kind: "chooser", candidates: websites };
    }
    return { kind: "known", website: sole };
  }

  return { kind: "chooser", candidates: websites };
};
